import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  CardHeaderControl,
  CardTitle,
  Heading,
  Link,
} from '@apollo/orbit';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { RouterHealthCard } from 'src/app/graph/cloudSettingsPage/components/cards/RouterHealthCard';
import { pluckCloudRouterEndpoint } from 'src/app/graph/cloudSettingsPage/helpers/pluckCloudRouterEndpoint';
import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { useCloudGCUMetricsQuery } from 'src/hooks/useCloudGcuMetricsQuery';
import { useCloudRoutingSettingsPageQuery } from 'src/hooks/useCloudRoutingSettingsPageQuery';
import { getRouterIsInitiating } from 'src/hooks/useIsCloudEndpointInitiating';
import { useTrackL1PageView } from 'src/lib/analytics/amplitude/hooks/useTrackL1PageView';
import Config from 'src/lib/config';
import { format } from 'src/lib/format';
import {
  CloudProvider,
  Resolution,
  RouterStatus,
} from 'src/lib/graphqlTypes/types';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { useGraphRef } from '../hooks/useGraphRef';

import { BarChartWrapper } from './BarChartWrapper';
import { RouterVersionCard } from './components/cards/RouterVersionCard';
import { metricPairs } from './helpers/metricPairs';
import { SetGcusModal } from './SetGcusModal';

// Define the modal that will be opened when the user clicks the "Manage Capacity" button
// set in the global Config object.
const {
  modals: { manageCapacity },
} = Config;

// Constant to represent 24 hours in seconds, used for querying metrics.
const TWENTY_FOUR_HOURS_IN_SECONDS = -86400;

const RESOLUTION = Resolution.R15M;

/**
 * CloudRoutingSettingsPage Component
 *
 * This component renders the details of the router and associated information.
 *
 * @returns {JSX.Element} Rendered component.
 */
export const CloudRoutingSettingsPage = () => {
  // Hooks for getting references to the graph, browser history, current account information, and location.
  const graphRef = useGraphRef();
  const location = useLocation();

  // GraphQL query to fetch routing settings related to the cloud.
  const { data, loading, refetch } = useCloudRoutingSettingsPageQuery(
    graphRef?.graphId ?? '',
    graphRef?.graphVariant ?? '',
    !graphRef,
  );

  // GraphQL query to fetch GraphOS Capacity Unit (GCU) metrics.
  const {
    data: gcuMetrics,
    loading: gcuMetricsLoading,
    error: gcuMetricsError,
  } = useCloudGCUMetricsQuery(
    graphRef?.graphVariant ?? '',
    graphRef?.graphId ?? '',
    TWENTY_FOUR_HOURS_IN_SECONDS,
    RESOLUTION,
    !graphRef,
  );

  // Check if the router is currently initiating. Default
  // to true until data is returned
  const routerIsInitiating = useMemo(
    () =>
      !data?.service?.variant ||
      getRouterIsInitiating({
        graphType: data.service.graphType,
        latestLaunch: data.service.variant?.latestLaunch,
        routerStatus: data.service.variant?.router?.status,
      }),
    [data?.service?.graphType, data?.service?.variant],
  );

  useTrackL1PageView('cloudRouterL1', { graphRef });

  // Available tracks (composition versions) for this service variant
  const availableTracksNonNull = useMemo(
    () =>
      data?.service?.variant?.allowedTracks?.filter(
        (t): t is NonNullable<typeof t> => !!t,
      ) ?? [],
    [data],
  );

  if (loading || gcuMetricsLoading) {
    return (
      <div className="grid h-full place-items-center">
        <Loading size="lg" />
      </div>
    );
  }

  if (!data?.service || !graphRef) {
    return <NotFound />;
  }

  if (!graphRef) {
    return <NotFound />;
  }

  // Extract graph variant, router status, versions, and other metrics from data
  const variant = data.service?.variant;
  const router = variant?.router;
  const routerStatus = router?.status; // Router status ("CREATED", "DELETING", or "DELETED")
  const routerVersion = router?.routerVersion;
  const routerId = router?.id ?? '';
  const tier = router?.shard?.tier;
  const isDedicatedTier = tier === 'DEDICATED';
  const federationVersion =
    (data?.service?.variant?.compositionVersion &&
      `v${data.service.variant?.compositionVersion}`) ||
    null;
  const gcus = router?.gcus ?? 0; // Router capacity measured in GCUs (Graph Compute Units)
  const routerRegion = router?.shard?.region;
  const routerProvider = routerRegion?.provider;
  const latestLaunch = variant?.latestLaunch;
  const endpoint: string | undefined = pluckCloudRouterEndpoint(router);

  const isAvailableAWSRouter =
    routerStatus !== RouterStatus.DELETED &&
    routerProvider === CloudProvider.AWS;

  return (
    <div className="mx-8 mt-4">
      <h1 className="mb-4 text-2xl font-semibold">Router details</h1>
      <div
        className="mb-12 flex flex-col
          lg:mr-2 lg:flex-row lg:items-start"
      >
        <div className="flex flex-1 flex-col gap-6 lg:flex-[2]">
          <RouterHealthCard
            isInitiating={routerIsInitiating}
            status={routerStatus}
            graphRef={graphRef}
            endpoint={endpoint}
            tier={tier}
          />
          <RouterVersionCard
            routerVersion={routerVersion}
            latestLaunch={latestLaunch}
            federationVersion={federationVersion}
            availableTracks={availableTracksNonNull}
            graphRef={graphRef}
            routerRegion={routerRegion}
          />
        </div>
        {/* GCU CARD BELOW */}
        <Card isDivided className="mt-6 lg:mt-0">
          <CardHeader>
            <CardTitle>
              Router capacity
              {/* Explanation for the condition below:
                If the current plan is not DEDICATED, users should see a tooltip.
                This tooltip informs them that Router Capacity is not available for their plan.
                It also provides a link to upgrade to a Dedicated plan. */}
              {!isDedicatedTier && (
                <Tooltip
                  interactive
                  label={
                    <div>
                      Router capacity is not available for Serverless plans.
                      <Link
                        to="https://apollographql.com/contact-sales?type=dedicated"
                        isExternal
                      >
                        Upgrade to a Dedicated plan
                      </Link>
                      to manage your router capacity.
                    </div>
                  }
                >
                  <IconInfo className="text-icon-primary" />
                </Tooltip>
              )}
            </CardTitle>
            {isDedicatedTier && isAvailableAWSRouter && (
              <PermissionGuard
                graphId={graphRef?.graphId}
                graphVariant={graphRef?.graphVariant}
                graphVariantPermissions="canManageCloudRouter"
              >
                <CardHeaderControl>
                  {routerStatus === RouterStatus.RUNNING ? (
                    <Link
                      to={setOverlayVisibleLocation({
                        location,
                        overlayName: Config.modals.manageCapacity,
                      })}
                    >
                      Manage capacity
                    </Link>
                  ) : (
                    <span className="text-secondary">
                      Manage capacity
                      <Tooltip label="The router is currently updating. Please try again later.">
                        <IconInfo className="ml-2 text-icon-primary" />
                      </Tooltip>
                    </span>
                  )}
                </CardHeaderControl>
              </PermissionGuard>
            )}
          </CardHeader>
          <CardBody className="justify-between md:flex-2">
            <div className="mb-8 flex items-center text-lg">
              {/* Explanation for the condition below:
                  The following section displays the Router Capacity information.
                  Users that are not on the DEDICATED plan should see a message about the
                  plan's limitation, along with a link to contact sales for an upgrade. */}
              {!isDedicatedTier && gcus < 1 ? (
                <>
                  <span className="text-lg font-semibold">Serverless Plan</span>
                  <Tooltip
                    interactive
                    label={
                      <div>
                        Graph Compute Units (GCUs) can only be added to
                        Dedicated plans.
                        <Link
                          to="https://apollographql.com/contact-sales?type=dedicated"
                          isExternal
                        >
                          Contact us
                        </Link>
                        to upgrade your plan to Dedicated.
                      </div>
                    }
                  >
                    <IconInfo className="ml-2 text-secondary" />
                  </Tooltip>
                </>
              ) : (
                <div>
                  <div className="mr-2 font-semibold">
                    {gcus}{' '}
                    {format.pluralize(
                      data?.service?.variant?.router?.gcus ?? 0,
                      'GCU',
                    )}
                  </div>
                </div>
              )}
            </div>
            <div>
              {isAvailableAWSRouter && (
                <div>
                  <Heading
                    as="h3"
                    size="lg"
                    className="mb-4 border-b border-b-primary pb-2 "
                  >
                    Cloud router usage (last 24 hours)
                  </Heading>
                  {gcuMetricsError && (
                    <Alert status="error" className="my-6">
                      We cannot retrieve your metrics at this time.
                    </Alert>
                  )}
                  {gcuMetrics &&
                    tier &&
                    metricPairs(tier).map((metricPair) => (
                      <BarChartWrapper
                        key={metricPair.primary}
                        data={gcuMetrics}
                        metricPair={metricPair}
                        yAxisLabel={metricPair.yAxisLabel}
                        loading={gcuMetricsLoading}
                      />
                    ))}
                </div>
              )}
            </div>
          </CardBody>
        </Card>
        {/* Modal to set GCUS */}
        <PermissionGuard
          graphId={graphRef?.graphId}
          graphVariant={graphRef?.graphVariant}
          graphVariantPermissions="canManageCloudRouter"
          fallback={null}
        >
          <LazyUrlModal name={manageCapacity}>
            {({ isOpen, closeModal }) => (
              <SetGcusModal
                gcus={gcus}
                routerId={routerId}
                isOpen={isOpen}
                onClose={closeModal}
                onUpdate={() => refetch()}
              />
            )}
          </LazyUrlModal>
        </PermissionGuard>
      </div>
    </div>
  );
};
